/**
 * Normalizes the `properties` JSON from an AgentRoutingSchema into a flat,
 * render-ready list of config fields. The backend `properties` shape is a JSON
 * scalar whose exact form is not guaranteed, so this handles the common shapes
 * defensively:
 *
 *  1. JSON-Schema object:    { properties: { account_id: { type, title } }, required: [...] }
 *  2. Bare properties map:   { account_id: { type: 'string', title: 'Account id' } }
 *  3. Flat field array:      [{ key|name, label|title, type, required }]
 *
 * Unknown/garbage input yields an empty list. Unknown field types fall back to
 * a text input.
 */
export type ConfigFieldType = 'text' | 'number' | 'select';

export interface ConfigField {
  key: string;
  label: string;
  type: ConfigFieldType;
  required: boolean;
  options?: string[];
}

const toLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const resolveType = (raw: unknown): ConfigFieldType => {
  const type = typeof raw === 'string' ? raw.toLowerCase() : '';
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'enum') return 'select';
  return 'text';
};

const fieldFromEntry = (
  key: string,
  spec: Record<string, unknown>
): ConfigField => {
  const options = Array.isArray(spec.enum)
    ? (spec.enum as unknown[]).map(String)
    : undefined;

  return {
    key,
    label: String(spec.title ?? spec.label ?? toLabel(key)),
    type: options ? 'select' : resolveType(spec.type),
    required: spec.required === true,
    options,
  };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const normalizeConfigSchema = (properties: unknown): ConfigField[] => {
  if (!properties) return [];

  // Shape 3: flat array of field descriptors.
  if (Array.isArray(properties)) {
    return properties
      .filter(isObject)
      .map((spec) => {
        const key = String(spec.key ?? spec.name ?? '');
        if (!key) return null;
        return {
          ...fieldFromEntry(key, spec),
          required: spec.required === true,
        };
      })
      .filter((field): field is ConfigField => field !== null);
  }

  if (!isObject(properties)) return [];

  // Shape 1: JSON-Schema object with nested `properties` + `required` list.
  const propsMap = isObject(properties.properties)
    ? (properties.properties as Record<string, unknown>)
    : properties;

  const requiredList = Array.isArray(properties.required)
    ? (properties.required as unknown[]).map(String)
    : [];

  return Object.entries(propsMap)
    .filter(([, spec]) => isObject(spec))
    .map(([key, spec]) => {
      const field = fieldFromEntry(key, spec as Record<string, unknown>);
      return {
        ...field,
        required: field.required || requiredList.includes(key),
      };
    });
};
